import { Injectable, OnDestroy } from '@angular/core';
import { isTextEntryElement, sanitizeInputValue } from 'src/app/shared/utils/strip-emoji';
import {
  isInvalidMobileStart,
  isMobilePhoneNumberField,
  // MOBILE_INVALID_START_MESSAGE,
} from 'src/app/shared/utils/validate-mobile';

/**
 * Globally sanitizes text entry:
 * - blocks paste/drop on all inputs/textareas
 * - always strips emoji (Win+.)
 * - then applies field rules (email / otp digits / letters / etc.)
 * - validates Indian mobile first digit (cannot start with 0-5)
 */
@Injectable({ providedIn: 'root' })
export class BlockEmojiService implements OnDestroy {
  private started = false;

  private readonly onBeforeInput = (event: Event) => {
    const e = event as InputEvent;
    const target = e.target;
    if (!isTextEntryElement(target)) {
      return;
    }

    // Block paste / drop insertions globally
    if (
      e.inputType === 'insertFromPaste' ||
      e.inputType === 'insertFromDrop' ||
      e.inputType === 'insertFromYank'
    ) {
      if (!target.hasAttribute('data-allow-paste')) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
    }

    if (e.data == null) {
      return;
    }

    const cleaned = sanitizeInputValue(target, e.data);
    if (cleaned === e.data) {
      return;
    }

    e.preventDefault();
    if (!cleaned) {
      return;
    }

    this.insertText(target, cleaned);
  };

  private readonly onInput = (event: Event) => {
    const target = event.target;
    if (!isTextEntryElement(target)) {
      return;
    }

    const original = target.value ?? '';
    const cleaned = sanitizeInputValue(target, original);
    if (cleaned !== original) {
      const start = target.selectionStart;
      target.value = cleaned;

      if (typeof start === 'number') {
        const nextPos = Math.min(start, cleaned.length);
        try {
          target.setSelectionRange(nextPos, nextPos);
        } catch {
          // Some input types may not support selection APIs.
        }
      }
    }

    this.applyMobileStartValidation(target);
  };

  private readonly onPaste = (event: Event) => {
    const target = event.target;
    if (!isTextEntryElement(target)) {
      return;
    }
    if (target.hasAttribute('data-allow-paste')) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  private readonly onDrop = (event: Event) => {
    const target = event.target;
    if (!isTextEntryElement(target)) {
      return;
    }
    if (target.hasAttribute('data-allow-paste')) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  start(): void {
    if (this.started || typeof document === 'undefined') {
      return;
    }
    this.started = true;
    document.addEventListener('beforeinput', this.onBeforeInput, true);
    document.addEventListener('input', this.onInput, true);
    document.addEventListener('paste', this.onPaste, true);
    document.addEventListener('drop', this.onDrop, true);
    document.addEventListener('compositionend', this.onInput, true);
  }

  stop(): void {
    if (!this.started || typeof document === 'undefined') {
      return;
    }
    this.started = false;
    document.removeEventListener('beforeinput', this.onBeforeInput, true);
    document.removeEventListener('input', this.onInput, true);
    document.removeEventListener('paste', this.onPaste, true);
    document.removeEventListener('drop', this.onDrop, true);
    document.removeEventListener('compositionend', this.onInput, true);
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private applyMobileStartValidation(el: HTMLInputElement | HTMLTextAreaElement): void {
    if (!isMobilePhoneNumberField(el)) {
      return;
    }

    const value = el.value ?? '';
    const parent = el.parentElement;
    let errEl = parent?.querySelector('[data-mobile-start-error]') as HTMLElement | null;

    if (isInvalidMobileStart(value)) {
      if (el instanceof HTMLInputElement) {
        // el.setCustomValidity(MOBILE_INVALID_START_MESSAGE);
      }
      if (!errEl) {
        errEl = document.createElement('div');
        errEl.setAttribute('data-mobile-start-error', '1');
        errEl.className = 'validation-error text-danger ft-12 mt-1';
        el.insertAdjacentElement('afterend', errEl);
      }
      // errEl.textContent = MOBILE_INVALID_START_MESSAGE;
      return;
    }

    if (el instanceof HTMLInputElement) {
      el.setCustomValidity('');
    }
    errEl?.remove();
  }

  private insertText(target: HTMLInputElement | HTMLTextAreaElement, text: string): void {
    const start = target.selectionStart ?? target.value.length;
    const end = target.selectionEnd ?? target.value.length;
    const merged = target.value.slice(0, start) + text + target.value.slice(end);
    const next = sanitizeInputValue(target, merged);
    target.value = next;

    const caret = Math.min(start + text.length, next.length);
    try {
      target.setSelectionRange(caret, caret);
    } catch {
      // ignore
    }

    target.dispatchEvent(new Event('input', { bubbles: true }));
  }
}
